import { Typography } from '@mui/material'
import { Modal } from '../../Components/Modal'
import { EnrollmentDetails } from '../../Components/Enrollment/EnrollmentDetails'
import { type ApprovalDisplayData, IncomeApprovalType } from './IncomeApprovalDetail'
import { useState } from 'react'
import { CheckBoxWithLabel } from '../../Components/CheckBox'
import { TextAreaWithLabel } from '../../Components/TextArea'
import AlertMessage from '../../Components/AlertMessage'
import { toast } from 'react-toastify'
import { sendPut } from '../../hooks/use-fetch'
import { type postError } from '../../core/types'

interface DeclineApprovalDialogProps {
  approvalInfo: ApprovalDisplayData
  onClose: (updated: boolean) => void
}

interface DeclineApprovalRequest {
  incomeApprovalID: number
  programWillEnroll: boolean
  comment: string | null
}

export function DeclineApprovalDialog (props: DeclineApprovalDialogProps): JSX.Element {
  const [request, setRequest] = useState<DeclineApprovalRequest>({ incomeApprovalID: props.approvalInfo.id, programWillEnroll: false, comment: null })

  const modalContent = <>
    <Typography variant='h6'>{props.approvalInfo.firstName} {props.approvalInfo.lastName}</Typography>
    <EnrollmentDetails enrollmentID={props.approvalInfo.enrollmentID ?? 0} canReview={false} readOnly={true} />
    {props.approvalInfo.type === IncomeApprovalType.Waiver &&
      <>
        <div style={{ marginTop: '10px' }}>
          <AlertMessage
            type='warning'
            message={`This action will also decline the referral for ${props.approvalInfo.verificationUserServiceProvider ?? ''}.  They will be notified in the system but you may want to reach out to ${props.approvalInfo.verificationUser} <${props.approvalInfo.verificationUserEmail}> to let them know.`}
            dismissible={false}
          />
        </div>
        <CheckBoxWithLabel
          label={`${props.approvalInfo.programName} is going to enroll this child`}
          value={request.programWillEnroll} name={''}
          setValue={function (newVal: boolean): void {
            setRequest({ ...request, programWillEnroll: newVal })
          }}
          edge={'start'}
        />
      </>
    }
    <TextAreaWithLabel label={'Comment'} name={'comment'} dataTestId='declineComment'
      value={request.comment}
      onChange={function (e: any): void {
        setRequest({ ...request, comment: e.target.value })
      }}

    />
  </>

  const handleConfirm = async (): Promise<void> => {
    const result = await sendPut('/IncomeApproval/DeclineApproval', request)
    const rsp: { newRecordID: number, success: boolean, errors: postError[] } = result.response

    if (rsp.success) {
      if (props.approvalInfo.type === IncomeApprovalType.Income) {
        toast.success('The Income Approval was declined and sent back to the requesting provider for review.')
      } else if (props.approvalInfo.type === IncomeApprovalType.Waiver && !request.programWillEnroll) {
        toast.success('The Waiver Approval was declined and sent back to the requesting provider for review.')
      } else if (props.approvalInfo.type === IncomeApprovalType.Waiver && request.programWillEnroll) {
        toast.success(`${props.approvalInfo.programName} is going to enroll this child`)
      }
      props.onClose(true)
    } else {
      rsp.errors.forEach((e: postError) => {
        if (e.error.startsWith('*')) {
          toast.warning(e.error.substring(1))
          props.onClose(true)
        } else {
          toast.error(e.error)
        }
      })
    }
  }

  return <Modal
    confirmationContent={modalContent}
    title={`Decline ${props.approvalInfo.type} Approval`}
    onClose={() => { props.onClose(false) }}
    open={true}
    onCancel={() => { props.onClose(false) }}
    onConfirm={handleConfirm}
  />
}
